import { type CSSProperties } from 'react';
import {
  formatYen,
  formatTaxRate,
  invoiceDocumentTypeLabel,
  type InvoiceDocument,
} from '@kawai/shared';

interface InvoiceDocumentViewProps {
  doc: InvoiceDocument;
}

/**
 * TP-26: 適格請求書 / 領収書の帳票本体（表示専用 / Web 印刷ビュー）。
 *
 * 料金は外税（契約 別紙1「消費税等は別途加算する」）。明細は**税抜本体価格**で表示し、
 * 「小計（税抜）＋ 消費税（税率ごと）＝ 合計（税込）」の外税レイアウトで描画する。
 * 適格請求書の必須記載事項——①発行事業者名＋登録番号 / ②取引年月日 / ③取引内容 /
 * ④税率ごとに区分した対価の額 / ⑤税率ごとの消費税額 / ⑥交付を受ける者の名称——を満たす。
 * 請求書 / 領収書はテンプレ共通で、表題・文言・金額ラベルのみ切替。
 */
export function InvoiceDocumentView({ doc }: InvoiceDocumentViewProps) {
  const title = invoiceDocumentTypeLabel(doc.type);
  const isReceipt = doc.type === 'receipt';
  const amountLabel = isReceipt ? '領収金額（税込）' : 'ご請求金額（税込）';

  return (
    <article style={sheetStyle}>
      <header style={headerStyle}>
        <h1 style={titleStyle}>{title}</h1>
        <div style={metaStyle}>
          <div>No. {doc.documentNumber}</div>
          <div>発行日: {doc.issuedDate}</div>
        </div>
      </header>

      <div style={partiesStyle}>
        {/* ⑥ 交付を受ける者の名称 */}
        <div style={recipientColStyle}>
          <div style={recipientNameStyle}>{doc.recipientName} 御中</div>
          {isReceipt && (
            <p style={receiptNoteStyle}>下記の金額を正に領収いたしました。</p>
          )}
        </div>
        {/* ① 発行事業者の名称＋登録番号 */}
        <div style={issuerColStyle}>
          <div style={issuerNameStyle}>{doc.issuer.name}</div>
          <div>登録番号: {doc.issuer.registrationNumber}</div>
          {doc.issuer.postalCode && <div>〒{doc.issuer.postalCode}</div>}
          {doc.issuer.address && <div>{doc.issuer.address}</div>}
          {doc.issuer.tel && <div>TEL: {doc.issuer.tel}</div>}
        </div>
      </div>

      {/* ② 取引年月日 */}
      <div style={txnDateStyle}>
        取引年月日: <span>{doc.transactionDate}</span>
      </div>

      {/* ③ 取引内容 / ④ 税率ごとに区分した対価（税抜本体価格で表示＝外税） */}
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>取引内容</th>
            <th style={thRightStyle}>税率</th>
            <th style={thRightStyle}>金額（税抜）</th>
          </tr>
        </thead>
        <tbody>
          {doc.lineItems.map((item, i) => (
            <tr key={i}>
              <td style={tdStyle}>{item.description}</td>
              <td style={tdRightStyle}>{formatTaxRate(item.taxRate)}</td>
              <td style={tdRightStyle}>{formatYen(item.netYen)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 小計（税抜） → ⑤ 税率ごとの消費税額 → 合計（税込） の外税表示 */}
      <div style={summaryStyle}>
        <div style={summaryRowStyle}>
          <span>小計（税抜）</span>
          <span style={amountCellStyle}>{formatYen(doc.totalNetYen)}</span>
        </div>
        {doc.taxSummary.map((t, i) => (
          <div key={i} style={summaryRowStyle}>
            <span>
              消費税（{formatTaxRate(t.taxRate)}対象 {formatYen(t.netYen)}）
            </span>
            <span style={amountCellStyle}>{formatYen(t.taxYen)}</span>
          </div>
        ))}
        <div style={totalRowStyle}>
          <span>{amountLabel}</span>
          <span style={amountCellStyle}>{formatYen(doc.totalGrossYen)}</span>
        </div>
      </div>

      {/* TP-27: 振込請求書のみ「お振込先」欄（銀行振込テナント / 領収書・カード決済は非表示） */}
      {doc.bankTransfer && (
        <div style={bankBoxStyle}>
          <div style={bankTitleStyle}>お振込先</div>
          {doc.bankTransfer.account ? (
            <div>
              <div>
                {doc.bankTransfer.account.bankName}
                {doc.bankTransfer.account.bankCode
                  ? `（${doc.bankTransfer.account.bankCode}）`
                  : ''}{' '}
                {doc.bankTransfer.account.branchName}
                {doc.bankTransfer.account.branchCode
                  ? `（${doc.bankTransfer.account.branchCode}）`
                  : ''}{' '}
                {doc.bankTransfer.account.accountType}{' '}
                {doc.bankTransfer.account.accountNumber}
              </div>
              <div>口座名義: {doc.bankTransfer.account.accountHolder}</div>
            </div>
          ) : (
            <div style={bankMissingStyle}>
              （振込先口座は未設定です — INVOICE_BANK_ACCOUNT を設定してください）
            </div>
          )}
          {doc.bankTransfer.dueDate && (
            <div style={bankDueStyle}>お支払期限: {doc.bankTransfer.dueDate}</div>
          )}
          {/* 振込手数料は甲（お客様）負担（利用契約 第6条3項） */}
          <div style={bankNoteStyle}>
            ※誠に恐れ入りますが、振込手数料は貴社にてご負担ください。
          </div>
        </div>
      )}

      {/* TP-34: 口座振替請求書のみ。「お振込先」欄は出さず、引き落としの旨のみ表示する
          （引落日・振替手数料の負担は経理確認で確定後に追記 / RUNBOOK §6.3h）。 */}
      {doc.directDebit && (
        <div style={bankBoxStyle}>
          <div style={bankTitleStyle}>お支払方法</div>
          <div>口座振替（自動引き落とし）</div>
          <div style={bankNoteStyle}>
            ※ご請求金額は、ご指定の預金口座からの口座振替によりお引き落としいたします。
            お振込みは不要です。
          </div>
        </div>
      )}
    </article>
  );
}

const sheetStyle: CSSProperties = {
  maxWidth: 720,
  margin: '0 auto',
  background: '#fff',
  color: '#111',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '2rem',
  fontFamily: 'system-ui, sans-serif',
  fontSize: '0.875rem',
  lineHeight: 1.6,
};
const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  borderBottom: '2px solid #111',
  paddingBottom: '0.75rem',
  marginBottom: '1.25rem',
};
const titleStyle: CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 700,
  letterSpacing: '0.3em',
  margin: 0,
};
const metaStyle: CSSProperties = {
  textAlign: 'right',
  fontSize: '0.8rem',
  color: '#374151',
};
const partiesStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '1.5rem',
  marginBottom: '1.25rem',
};
const recipientColStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};
const recipientNameStyle: CSSProperties = {
  fontSize: '1.05rem',
  fontWeight: 600,
  borderBottom: '1px solid #9ca3af',
  paddingBottom: '0.25rem',
};
const receiptNoteStyle: CSSProperties = {
  marginTop: '0.75rem',
  fontSize: '0.8rem',
  color: '#374151',
};
const issuerColStyle: CSSProperties = {
  textAlign: 'right',
  fontSize: '0.8rem',
  color: '#374151',
  whiteSpace: 'nowrap',
};
const issuerNameStyle: CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#111',
  marginBottom: '0.15rem',
};
const txnDateStyle: CSSProperties = {
  fontSize: '0.8rem',
  color: '#374151',
  marginBottom: '0.5rem',
};
const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  marginBottom: '1rem',
};
const thStyle: CSSProperties = {
  textAlign: 'left',
  borderBottom: '1px solid #111',
  borderTop: '1px solid #111',
  padding: '0.5rem',
  fontSize: '0.8rem',
  background: '#f9fafb',
};
const thRightStyle: CSSProperties = { ...thStyle, textAlign: 'right' };
const tdStyle: CSSProperties = {
  padding: '0.5rem',
  borderBottom: '1px solid #e5e7eb',
};
const tdRightStyle: CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  whiteSpace: 'nowrap',
};
const summaryStyle: CSSProperties = {
  marginLeft: 'auto',
  width: '60%',
  minWidth: 280,
};
const summaryRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '1rem',
  padding: '0.3rem 0.5rem',
  fontSize: '0.8rem',
};
const totalRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '1rem',
  padding: '0.6rem 0.5rem',
  marginTop: '0.25rem',
  borderTop: '2px solid #111',
  fontSize: '1rem',
  fontWeight: 700,
};
const amountCellStyle: CSSProperties = {
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
};
// TP-27: お振込先欄（帳票は印刷用に固定 hex）
const bankBoxStyle: CSSProperties = {
  marginTop: '1.25rem',
  border: '1px solid #9ca3af',
  borderRadius: 6,
  padding: '0.75rem 1rem',
  fontSize: '0.85rem',
  lineHeight: 1.7,
};
const bankTitleStyle: CSSProperties = {
  fontWeight: 700,
  marginBottom: '0.25rem',
};
const bankMissingStyle: CSSProperties = {
  color: '#b91c1c',
};
const bankDueStyle: CSSProperties = {
  marginTop: '0.25rem',
};
const bankNoteStyle: CSSProperties = {
  marginTop: '0.25rem',
  fontSize: '0.75rem',
  color: '#374151',
};
